import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import DataList from '../formx/DataList';
import { ArgMap, FieldConfig } from '../formx/FormTypes';
import { UserRecord } from '../shared/OurGateTypes';
import { getAuth } from 'firebase/auth';
import { getApp } from '../fire';

interface UserListRecord extends UserRecord, ArgMap {
  duplicateStatus: string;
  activationStatus: string;
  superAdminStatus: string;
}

const listConfig = {
  name: 'users',
  idcol: 'userId',
  dispcols: ['email', 'name', 'superAdminStatus', 'activationStatus', 'userId', 'duplicateStatus'],
  editcols: [],
};

const fieldConfig: FieldConfig = {
  email: {
    type: 'email',
    title: 'Email',
  },
  name: {
    type: 'string',
    title: 'Name',
  },
  userId: {
    type: 'string',
    title: 'User ID',
  },
  duplicateStatus: {
    type: 'string',
    title: 'Duplicate Email',
  },
  activationStatus: {
    type: 'string',
    title: 'Activation',
  },
  superAdminStatus: {
    type: 'string',
    title: 'Super Admin',
  },
};

export const identifyDuplicateEmails = (users: UserRecord[]): UserListRecord[] => {
  const emailGroups = new Map<string, UserRecord[]>();

  users.forEach((user) => {
    const normalizedEmail = user.email.trim().toLowerCase();
    const group = emailGroups.get(normalizedEmail) || [];
    group.push(user);
    emailGroups.set(normalizedEmail, group);
  });

  return users.map((user) => {
    const group = emailGroups.get(user.email.trim().toLowerCase()) || [];
    const variants = new Set(group.map((item) => item.email));
    let duplicateStatus = '';
    if (group.length > 1) {
      duplicateStatus =
        variants.size > 1 ? `Case variants (${group.length} records)` : `Duplicate (${group.length} records)`;
    }
    const activationStatus = getActivationStatus(user);
    const superAdminStatus = user.superAdmin === true ? 'Yes' : '';
    return { ...user, duplicateStatus, activationStatus, superAdminStatus };
  });
};

export const getActivationStatus = (user: UserRecord) => {
  if (user.activatedAt) {
    return `Activated ${new Date(user.activatedAt).toLocaleString()}`;
  }
  if (user.activationCode) {
    return 'Code sent';
  }
  if (user.activatedAt === undefined && user.activationExpiresAt > 0) {
    return 'Activated';
  }
  return user.activatedAt === 0 ? 'Not activated' : 'Unknown';
};

export default function UserList() {
  const [users, setUsers] = useState<UserRecord[]>();
  const [userToDelete, setUserToDelete] = useState<ArgMap>();
  const loadUsers = useCallback(async () => {
    const token = await getAuth(getApp()).currentUser?.getIdToken();
    if (!token) {
      throw new Error('Unable to authenticate the user-list request.');
    }
    const response = await fetch('/api/exec', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ op: 'list-users' }),
    });
    const result = await response.json();
    if (!response.ok || result.status !== 'OK') {
      throw new Error(result.error || 'Unable to load users.');
    }
    setUsers(result.users);
  }, []);
  useEffect(() => {
    loadUsers().catch((err) => window.alert(err instanceof Error ? err.message : 'Unable to load users.'));
  }, [loadUsers]);
  const initialized = users !== undefined;
  const rows = identifyDuplicateEmails(users || []);
  const duplicateRows = rows.filter((user) => user.duplicateStatus);
  const duplicateGroups = new Set(duplicateRows.map((user) => user.email.trim().toLowerCase())).size;

  const onDeleteRow = (values: ArgMap) => {
    setUserToDelete(values);
  };

  const deleteUser = async () => {
    const userId = String(userToDelete?.userId || '');
    if (!userId) {
      window.alert('Unable to delete user without a user ID.');
      setUserToDelete(undefined);
      return;
    }
    try {
      const token = await getAuth(getApp()).currentUser?.getIdToken();
      if (!token) {
        throw new Error('Unable to authenticate the delete request.');
      }
      const response = await fetch('/api/exec', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ op: 'delete-user', userId }),
      });
      const result = await response.json();
      if (!response.ok || result.status !== 'OK') {
        throw new Error(result.error || 'Unable to delete user.');
      }
      await loadUsers();
      setUserToDelete(undefined);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Unable to delete user.');
    }
  };

  if (!initialized) {
    return <CircularProgress />;
  }

  return (
    <Stack spacing={2}>
      <Typography component="h1" variant="h5">
        Users
      </Typography>
      {duplicateGroups > 0 ? (
        <Alert severity="warning">
          Found {duplicateGroups} case-insensitive duplicate email group{duplicateGroups === 1 ? '' : 's'} affecting{' '}
          {duplicateRows.length} user records.
        </Alert>
      ) : (
        <Alert severity="success">No case-insensitive duplicate emails found.</Alert>
      )}
      <DataList<UserListRecord>
        title="All User Records"
        listConfig={listConfig}
        fieldConfig={fieldConfig}
        canDelete={true}
        showCount={true}
        rows={rows}
        onDelete={onDeleteRow}
        orderBy="email"
      />
      <Dialog open={Boolean(userToDelete)} onClose={() => setUserToDelete(undefined)}>
        <DialogTitle>Delete User?</DialogTitle>
        <DialogContent>
          Delete "{String(userToDelete?.email || userToDelete?.userId || '')}"? This removes the global user record and
          removes the email from every site.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserToDelete(undefined)}>Cancel</Button>
          <Button color="error" onClick={deleteUser}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
